import { useState } from 'react';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Box from '@mui/material/Box';
import PanelSkeleton from '@shared/components/PanelSkeleton';
import RetryableError from '@shared/components/RetryableError';
import { ApiRequestError } from '@api/client/apiClient';
import { useOperations } from './hooks/useOperations';
import WeeklyUpdatePanel from './components/WeeklyUpdatePanel';
import OnDemandRequestForm from './components/OnDemandRequestForm';
import LicensesList from './components/LicensesList';
import OperationsTable from './components/OperationsTable';
import UpdatesJournal from './components/UpdatesJournal';

type UpdatesTab = 'weekly' | 'on-demand' | 'licenses' | 'operations' | 'journal';

export default function UpdatesPage() {
  const [activeTab, setActiveTab] = useState<UpdatesTab>('weekly');
  const { data, isLoading, error, refetch } = useOperations();

  if (isLoading) return <PanelSkeleton />;

  if (error && !data) {
    const correlationId =
      error instanceof ApiRequestError ? error.apiError.correlationId : undefined;
    return (
      <RetryableError
        message={error.message ?? 'Failed to load operations'}
        correlationId={correlationId}
        onRetry={() => void refetch()}
      />
    );
  }

  const operations = data?.items ?? [];
  const lastWeeklyOp = operations.find(
    (op) => op.type === 'weekly' && (op.status === 'succeeded' || op.status === 'failed'),
  );

  return (
    <div data-testid="page-updates" className="flex flex-col h-full p-4 gap-3">
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={(_, val: UpdatesTab) => setActiveTab(val)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab value="weekly" label="Weekly" />
          <Tab value="on-demand" label="On-Demand" />
          <Tab value="licenses" label="Licenses" />
          <Tab value="operations" label="Operations" />
          <Tab value="journal" label="Journal" />
        </Tabs>
      </Box>

      <div className="flex-1 overflow-y-auto">
        {activeTab === 'weekly' && <WeeklyUpdatePanel lastWeeklyOp={lastWeeklyOp} />}
        {activeTab === 'on-demand' && <OnDemandRequestForm />}
        {activeTab === 'licenses' && <LicensesList />}
        {activeTab === 'operations' && (
          <OperationsTable operations={operations} loading={isLoading} />
        )}
        {activeTab === 'journal' && <UpdatesJournal />}
      </div>
    </div>
  );
}
